import { useEffect, useRef, useState } from 'react';
import { NJButton, NJFormItem, NJIconButton, NJSegmentedControl, NJSegmentedControlButton, NJSelect } from '@engie-group/fluid-design-system-react';
import { PLANNING_LEGEND, type PlanningConstraint } from '@shared/size2mix-shell';
import { Gantt, PlanningLegend } from './Gantt';
import { usePlanningMutations } from './hooks';
import type { GanttRow, Planning, PlanningKind, PlanningView } from './types';

const KINDS: PlanningConstraint['kind'][] = ['Indisponible', 'Minimum', 'Maximum', 'Forcé à', 'Démarrage interdit'];

export default function PlanningPanel({ kind, plannings, rows }: { kind: PlanningKind; plannings: Planning[]; rows: GanttRow[] }) {
    const [planningId, setPlanningId] = useState(plannings[0]?.id ?? '');
    const [view, setView] = useState<PlanningView>('all');
    const [adding, setAdding] = useState(false);
    const { addConstraint, deleteConstraint } = usePlanningMutations(kind);
    const planning = plannings.find((p) => p.id === planningId) ?? plannings[0];
    const targets = rows.filter((r) => r.level === 2 || kind === 'topology').map((r) => r.label);

    // A planning created from the tab-row button is appended: select it.
    const previousCount = useRef(plannings.length);
    useEffect(() => {
        if (plannings.length > previousCount.current) setPlanningId(plannings[plannings.length - 1].id);
        previousCount.current = plannings.length;
    }, [plannings]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 239 }}>
                    <NJSelect.Root
                        key={plannings.length}
                        id={`ref-planning-${kind}`}
                        label="Planning capturé"
                        labelKind="floating"
                        listNavigationLabel="Flèches + Entrée"
                        buttonDefaultValueLabel="Choisir un planning"
                        initialValue={planning?.id}
                        onChange={(v) => v && setPlanningId(v)}
                    >
                        {plannings.map((p) => (
                            <NJSelect.Item key={p.id} value={p.id}>
                                {p.name}
                            </NJSelect.Item>
                        ))}
                    </NJSelect.Root>
                </div>
                <span style={{ flex: 1 }} />
                <NJIconButton icon="delete_outline" label="Supprimer le planning" variant="tertiary" />
            </div>

            {planning && (
                <div style={{ background: 'var(--nj-semantic-color-background-neutral-primary-default)', padding: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', fontSize: 16 }}>
                        <span>Affichage</span>
                        <NJSegmentedControl label="Affichage" size="sm" value={view} onChange={(v) => setView(v as PlanningView)}>
                            <NJSegmentedControlButton value="day" label="Jour" />
                            <NJSegmentedControlButton value="week" label="Semaine" />
                            <NJSegmentedControlButton value="month" label="Mois" />
                            <NJSegmentedControlButton value="year" label="Année" />
                            <NJSegmentedControlButton value="all" label="Toute période" />
                        </NJSegmentedControl>
                        <span style={{ marginLeft: 'auto' }}>Nombre de contraintes : {planning.constraints.length}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                        <NJButton label="Ajouter une contrainte" scale="sm" emphasis="subtle" onClick={() => setAdding((a) => !a)} />
                        <NJIconButton icon="fullscreen" label="Plein écran" variant="tertiary" scale="sm" />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, margin: '32px 0 24px', fontSize: 16 }}>
                        <span>Légende</span>
                        <PlanningLegend items={PLANNING_LEGEND} />
                    </div>
                    {adding && (
                        <AddConstraintForm
                            targets={targets}
                            onCancel={() => setAdding(false)}
                            onSubmit={(constraint) => {
                                addConstraint.mutate({ planningId: planning.id, constraint });
                                setAdding(false);
                            }}
                        />
                    )}
                    <p style={{ fontSize: 14, lineHeight: '21px', margin: '0 0 8px' }}>Utilisez le clic gauche ou droit sur un intervalle pour accéder aux fonctionnalités d'édition</p>
                    <Gantt rows={rows} constraints={planning.constraints} view={view} onDelete={(id) => deleteConstraint.mutate({ planningId: planning.id, id })} />
                    {planning.datesCaptured === false && <p role="note">Le nom du planning et les dates de la contrainte ne figurent pas dans la capture ; son positionnement est illustratif.</p>}
                </div>
            )}
        </div>
    );
}

function AddConstraintForm({
    targets,
    onSubmit,
    onCancel,
}: {
    targets: string[];
    onSubmit: (c: Omit<PlanningConstraint, 'id'>) => void;
    onCancel: () => void;
}) {
    const [target, setTarget] = useState(targets[0] ?? '');
    const [kind, setKind] = useState<PlanningConstraint['kind']>('Indisponible');
    const [start, setStart] = useState('2026-03-01');
    const [end, setEnd] = useState('2026-03-31');
    const [value, setValue] = useState('');
    const needsValue = kind !== 'Indisponible' && kind !== 'Démarrage interdit';

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                onSubmit({ target, kind, start, end, value: needsValue ? Number(value) : undefined });
            }}
            style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 12,
                flexWrap: 'wrap',
                padding: 16,
                marginBottom: 16,
                background: 'var(--nj-semantic-color-background-neutral-secondary-default)',
            }}
        >
            <div style={{ width: 240 }}>
                <NJSelect.Root id="ref-c-target" label="Élément" labelKind="static" listNavigationLabel="Flèches + Entrée" buttonDefaultValueLabel="Élément" initialValue={target} onChange={(v) => v && setTarget(v)}>
                    {targets.map((t) => (
                        <NJSelect.Item key={t} value={t}>
                            {t}
                        </NJSelect.Item>
                    ))}
                </NJSelect.Root>
            </div>
            <div style={{ width: 200 }}>
                <NJSelect.Root id="ref-c-kind" label="Type" labelKind="static" listNavigationLabel="Flèches + Entrée" buttonDefaultValueLabel="Type" initialValue={kind} onChange={(v) => setKind(v as PlanningConstraint['kind'])}>
                    {KINDS.map((k) => (
                        <NJSelect.Item key={k} value={k}>
                            {k}
                        </NJSelect.Item>
                    ))}
                </NJSelect.Root>
            </div>
            <div style={{ width: 170 }}>
                <NJFormItem id="ref-c-start" label="Début" labelKind="static" type="date" value={start} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStart(e.target.value)} />
            </div>
            <div style={{ width: 170 }}>
                <NJFormItem id="ref-c-end" label="Fin" labelKind="static" type="date" value={end} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEnd(e.target.value)} />
            </div>
            {needsValue && (
                <div style={{ width: 130 }}>
                    <NJFormItem id="ref-c-value" label="Valeur [MW]" labelKind="static" type="number" value={value} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)} />
                </div>
            )}
            <NJButton type="submit" label="Ajouter" disabled={!target || !start || !end || end < start || (needsValue && !value)} />
            <NJButton type="button" label="Annuler" emphasis="minimal" onClick={onCancel} />
        </form>
    );
}
